import * as React from 'react';

import { cn } from '../lib/cn';

export type SkeletonShape = 'text' | 'text-3' | 'paragraph' | 'row' | 'card' | 'circle' | 'box';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  shape?: SkeletonShape;
  /** For shape="text-N" override line count (default 3) */
  lines?: number;
}

const shimmer =
  'relative overflow-hidden bg-[var(--color-subtle)] before:absolute before:inset-0 before:-translate-x-full before:animate-[teivor-shimmer_1.4s_ease-in-out_infinite] before:bg-gradient-to-r before:from-transparent before:via-[rgba(255,255,255,0.06)] before:to-transparent';

const Bar = ({ className }: { className?: string }) => (
  <div className={cn('h-3 rounded-md', shimmer, className)} />
);

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  ({ shape = 'text', lines, className, style, ...props }, ref) => {
    if (shape === 'circle') {
      return (
        <div
          ref={ref}
          className={cn('rounded-full', shimmer, className)}
          style={{ width: 36, height: 36, ...style }}
          {...props}
        />
      );
    }
    if (shape === 'box') {
      return <div ref={ref} className={cn('rounded-md', shimmer, className)} style={style} {...props} />;
    }
    if (shape === 'row') {
      return (
        <div ref={ref} className={cn('flex items-center gap-3 py-2', className)} {...props}>
          <div className={cn('h-4 w-4 rounded', shimmer)} />
          <Bar className="h-4 flex-1" />
          <Bar className="h-4 w-24" />
          <Bar className="h-4 w-16" />
        </div>
      );
    }
    if (shape === 'card') {
      return (
        <div
          ref={ref}
          className={cn(
            'rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5',
            className,
          )}
          {...props}
        >
          <div className="flex items-center gap-3">
            <div className={cn('h-10 w-10 rounded-full', shimmer)} />
            <div className="flex-1 space-y-2">
              <Bar className="h-4 w-1/3" />
              <Bar className="h-3 w-1/2" />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <Bar className="h-3 w-full" />
            <Bar className="h-3 w-11/12" />
            <Bar className="h-3 w-3/4" />
          </div>
        </div>
      );
    }
    if (shape === 'paragraph') {
      return (
        <div ref={ref} className={cn('space-y-2', className)} {...props}>
          <Bar className="h-3 w-full" />
          <Bar className="h-3 w-11/12" />
          <Bar className="h-3 w-10/12" />
          <Bar className="h-3 w-9/12" />
          <Bar className="h-3 w-7/12" />
        </div>
      );
    }
    if (shape === 'text-3') {
      return (
        <div ref={ref} className={cn('space-y-2', className)} {...props}>
          <Bar className="h-3 w-full" />
          <Bar className="h-3 w-10/12" />
          <Bar className="h-3 w-7/12" />
        </div>
      );
    }
    const count = lines ?? 1;
    return (
      <div ref={ref} className={cn('space-y-2', className)} {...props}>
        {Array.from({ length: count }).map((_, i) => (
          <Bar key={i} className={i === count - 1 && count > 1 ? 'w-2/3' : 'w-full'} />
        ))}
      </div>
    );
  },
);
Skeleton.displayName = 'Skeleton';
